import { SituacaoProblema } from 'graphql/types.generated'
import { useMemo } from 'react'

import { ProblemaCondicaoModel } from '../../avaliacao/components/problemas-condicoes/model'
import { EvolucaoProblema, Problema } from '../types/ProblemaModel'

export default (problemas: Problema[], problemasAvaliados: ProblemaCondicaoModel[]) =>
  useMemo(() => {
    const getProblemasAtivosLatentes = () => filterAndSortProblemas(problemas, problemasAvaliados ?? [])
    const getProblemasResolvidos = () => filterAndSortProblemas(problemas, problemasAvaliados ?? [], true)

    const gruposProblemas = breakProblemas(getProblemasAtivosLatentes())
    const getProblemasExpandido = () => gruposProblemas.problemasExpandido
    const getProblemasAccordion = () => gruposProblemas.problemasAccordion

    return { getProblemasExpandido, getProblemasAccordion, getProblemasAtivosLatentes, getProblemasResolvidos }
  }, [problemas, problemasAvaliados])

/**
 *
 * @param {Problema[]} problemas lista de todos os problemas
 * @param {ProblemaCondicaoModel[]} problemasAvaliados lista de problemas avaliados no atendimento atual
 * @param {boolean} isResolvido se true retornará os problemas RESOLVIDOS, senão retornará os problemas ATIVOS e LATENTES
 * @returns {Problema[]} lista de problemas avaliados, ativos, latentes e resolvidos ordenados de forma decrescente em relação a data da última atualização
 */
export const filterAndSortProblemas = (
  problemas: Problema[],
  problemasAvaliados: ProblemaCondicaoModel[],
  isResolvido: boolean = false
): Problema[] =>
  problemas
    .concat(extractNovosProblemas(problemasAvaliados))
    .map((problema) =>
      mergeAvaliacao(
        problema,
        problemasAvaliados.find((pa) => pa.incluirNaListaProblemas && pa.problemaId && pa.problemaId === problema.id)
      )
    )
    .filter(
      (problema) =>
        problema &&
        (isResolvido
          ? problema.situacao === SituacaoProblema.RESOLVIDO
          : problema.situacao !== SituacaoProblema.RESOLVIDO)
    )
    .sort((a, b) => (b.ultimaEvolucao.atualizadoEm ?? Date.now()) - (a.ultimaEvolucao.atualizadoEm ?? Date.now()))

/**
 * Se o total de problemas for menor ou igual a 6, todas devem ser apresentadas no grupo expandido e o accordion não deve ser exibido
 * Se o total de problemas for 6 ou +, no grupo de expandidos deve possuir apenas 5 problemas e o restante deve ficar dentro do accordion
 * @param {Problema[]} problemasAtivosLatentes lista de problemas ativos e latentes
 * @returns {{problemasExpandido: Problema[]; problemasAccordion: Problema[]}} listas de problemas a serem apresentados no card e no accordion
 */
export const breakProblemas = (problemasAtivosLatentes: Problema[]) => {
  const totalProblemas = problemasAtivosLatentes.length
  const problemasExpandido: Problema[] = []
  const problemasAccordion: Problema[] = []

  totalProblemas > 6 && problemasAccordion.push(...problemasAtivosLatentes.splice(5, totalProblemas - 1))
  problemasExpandido.push(...problemasAtivosLatentes)

  return { problemasExpandido, problemasAccordion }
}

/**
 *
 * @param {Problema} problema
 * @param {ProblemaCondicaoModel} avaliacao
 */
const mergeAvaliacao = (problema: Problema, avaliacao: ProblemaCondicaoModel): Problema =>
  avaliacao
    ? {
        ...convertAvaliacaoToProblema(avaliacao),
        evolucoes: [convertAvaliacaoToEvolucao(avaliacao), ...(problema.evolucoes ?? [])],
      }
    : problema

/**
 *
 * @param {EvolucaoProblema} avaliacao
 */
const convertAvaliacaoToEvolucao = (avaliacao: ProblemaCondicaoModel): EvolucaoProblema => ({
  id: avaliacao._id,
  situacao: avaliacao.situacaoProblema,
  dataInicio: avaliacao.dataInicio?.data,
  dataFim: avaliacao.dataFim?.data,
  observacao: avaliacao.observacao,
})

/**
 *
 * @param {ProblemaCondicaoModel[]} problemasAvaliados lista de problemas avaliados no atendimento atual
 * @returns {Problema[]} lista de novos problemas a ser adicionados na lista de problemas
 */
const extractNovosProblemas = (problemasAvaliados: ProblemaCondicaoModel[]): Problema[] =>
  problemasAvaliados.filter((pa) => pa.incluirNaListaProblemas && !pa.problemaId).map(convertAvaliacaoToProblema)

/**
 *
 * @param {ProblemaCondicaoModel} avaliacao
 * @returns {Problema}
 */
function convertAvaliacaoToProblema(avaliacao: ProblemaCondicaoModel): Problema {
  const evolucao = convertAvaliacaoToEvolucao(avaliacao)
  return {
    id: avaliacao.problemaId ?? avaliacao._id,
    ciap: avaliacao.ciap && {
      id: avaliacao.ciap.id,
      codigo: avaliacao.ciap.codigo,
      descricao: avaliacao.ciap.nome,
    },
    cid10: avaliacao.cid && {
      id: avaliacao.cid.id,
      codigo: avaliacao.cid.codigo,
      nome: avaliacao.cid.nome,
    },
    descricaoOutro: avaliacao.problemaCondicaoEvoluir?.descricaoOutro,
    evolucoes: [evolucao],
    situacao: avaliacao.situacaoProblema,
    ultimaEvolucao: evolucao,
    avaliadoAgora: true,
  }
}
